import { useEffect, useState } from "react";
import { Icons } from "./icons";
import { Button } from "./ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { Label } from "./ui/label";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import {
  Elements,
  LinkAuthenticationElement,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { Alert, AlertDescription, AlertTitle } from "./ui/alert";
import { AlertCircle, Terminal } from "lucide-react";
import { STRIPE_PUBLIC_KEY } from "../lib/constants";
import { useLocation } from "react-router-dom";

// Only load Stripe if we have a valid public key
const stripePromise = STRIPE_PUBLIC_KEY && STRIPE_PUBLIC_KEY.trim() !== '' 
  ? loadStripe(STRIPE_PUBLIC_KEY) 
  : null;

function PaymentMethodsFormInner({ oldPayment: paymentDetails }) {
  const [clientSecret, setClientSecret] = useState(null);
  const stripe = useStripe();
  const elements = useElements();
  const StripeIcon = Icons.stripe;

  useEffect(() => {
    // Extract client_secret from the URL
    const urlParams = new URLSearchParams(window.location.search);
    const secret = urlParams.get("client_secret");
    if (secret) {
      setClientSecret(secret);
    }
  }, []);

  const [message, setMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      // Stripe.js has not yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return;
    }

    setIsLoading(true);

    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        // Make sure to change this to your payment completion page
        return_url: `${window.location.origin}/completion`,
      },
    });

    // This point will only be reached if there is an immediate error when
    // confirming the payment. Otherwise, your customer will be redirected to
    // your `return_url`. For some payment methods like iDEAL, your customer will
    // be redirected to an intermediate site first to authorize the payment, then
    // redirected to the `return_url`.
    if (error.type === "card_error" || error.type === "validation_error") {
      setMessage(error.message);
    } else {
      setMessage("An unexpected error occured.");
    }

    setIsLoading(false);
  };
  return (
    <>
      {paymentDetails?.hasPaid ? (
        <Alert className="max-w-96">
          <Terminal className="h-4 w-4" />
          <AlertTitle>Subscription Notice</AlertTitle>
          <AlertDescription>
            <>
              <p>
                Your subscription for the <strong>{paymentDetails.tier}</strong>{" "}
                tier ({paymentDetails.frequency}) is active.
              </p>
              <p>
                Your last payment was on{" "}
                <strong>
                  {new Date(paymentDetails.paidAt).toLocaleDateString()}
                </strong>
                , and your subscription will renew on{" "}
                <strong>
                  {new Date(
                    paymentDetails.subscriptionExpiresAt
                  ).toLocaleDateString()}
                </strong>
                .
              </p>
              <p>
                Please ensure your payment method is up to date for the next
                cycle.
              </p>
            </>
          </AlertDescription>
        </Alert>
      ) : null}
      <Card className="min-w-96 max-w-[450px]">
        <CardHeader>
          <CardTitle>Payment Method</CardTitle>
          <CardDescription>
            Add a new payment method to your account.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-6">
          <RadioGroup defaultValue="stripe" className="grid grid-cols-2 gap-4">
            <div>
              <RadioGroupItem
                value="stripe"
                id="stripe"
                className="peer sr-only"
                aria-label="Stripe"
              />
              <Label
                htmlFor="stripe"
                className="flex flex-col items-center justify-between rounded-md border-2 border-muted bg-transparent p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary "
              >
                <StripeIcon className="mb-3 h-6 w-6" />
                Stripe
              </Label>
            </div>
          </RadioGroup>
          {clientSecret && (
            <form onSubmit={handleSubmit} className="grid gap-4">
              <LinkAuthenticationElement id="link-authentication-element" />
              <PaymentElement id="payment-element" />
              <Button
                disabled={isLoading || !stripe || !elements}
                id="submit"
                className="rounded-xl"
              >
                Continue
              </Button>
              {/* Show any error or success messages */}
              {message && (
                <Alert className="mt-3">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Error</AlertTitle>
                  <AlertDescription>{message}</AlertDescription>
                </Alert>
              )}
            </form>
          )}
        </CardContent>
      </Card>
    </>
  );
}

const PaymentMethodsForm = () => {
  const [clientSecret, setClientSecret] = useState(null);
  const [paymentDetails, setPaymentDetails] = useState({
    hasPaid: null,
    paidAt: null,
    subscriptionExpiresAt: null,
    tier: null,
    frequency: null,
  });
  const location = useLocation();

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);

    // Extract client_secret
    const secret = queryParams.get("client_secret");
    if (secret) {
      setClientSecret(secret);
    }

    // Extract other payment details
    setPaymentDetails({
      hasPaid: queryParams.get("has_paid") === "true",
      paidAt: queryParams.get("paid_at"),
      subscriptionExpiresAt: queryParams.get("subscription_expires_at"),
      tier: queryParams.get("tier"),
      frequency: queryParams.get("frequency"),
    });
  }, [location.search]);

  // Check if Stripe is properly configured
  if (!STRIPE_PUBLIC_KEY || STRIPE_PUBLIC_KEY.trim() === '') {
    return (
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Configuration Error</AlertTitle>
        <AlertDescription>
          Stripe is not properly configured. Please contact support or try again later.
        </AlertDescription>
      </Alert>
    );
  }

  if (!stripePromise) {
    return (
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Payment Service Unavailable</AlertTitle>
        <AlertDescription>
          Payment processing is currently unavailable. Please try again later.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    clientSecret && (
      <Elements stripe={stripePromise} options={{ clientSecret }}>
        <PaymentMethodsFormInner oldPayment={paymentDetails} />
      </Elements>
    )
  );
};

export default PaymentMethodsForm;
